import { Box, Paper, Popper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

interface TutorialSpotlightProps {
  targetSelector: string;
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'inside-top' | 'inside-bottom';
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip?: () => void;
  onElementNotFound?: () => void;
  showPrevious?: boolean;
  children?: React.ReactNode;
}

const TutorialSpotlight: React.FC<TutorialSpotlightProps> = ({
  targetSelector,
  title,
  content,
  position = 'right',
  onNext,
  onPrevious,
  onSkip,
  onElementNotFound,
  showPrevious = false,
  children,
}) => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reset state when targetSelector changes
    setTargetElement(null);
    setTargetRect(null);

    let currentElement: HTMLElement | null = null;
    let retryPhase: 'initial' | 'delayed' = 'initial';
    let delayedAttempts = 0;
    const maxDelayedAttempts = 8; // 8 attempts in second phase
    const initialDelay = 500; // First try after 500ms
    const delayBeforeRetry = 2000; // Wait 2s before starting delayed retries
    const delayedRetryInterval = 800; // 800ms between delayed retries

    const findElement = () => {
      const element = document.querySelector(targetSelector) as HTMLElement;
      if (element) {
        currentElement = element;

        // Elevate target element above the overlay
        // Only set position to relative if element doesn't already have absolute/fixed positioning
        const computedPosition = window.getComputedStyle(element).position;
        if (computedPosition === 'static') {
          element.style.position = 'relative';
        }
        element.style.zIndex = '9999';

        // Scroll element into view FIRST, then capture rect after scroll completes
        element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });

        // Wait for scroll to complete and layout to settle before getting rect
        setTimeout(() => {
          requestAnimationFrame(() => {
            const rect = element.getBoundingClientRect();
            setTargetElement(element);
            setTargetRect(rect);
          });
        }, 100);
      } else {
        // Element not found
        if (retryPhase === 'initial') {
          // First attempt failed, wait 2s then start delayed retries
          retryPhase = 'delayed';
          setTimeout(findElement, delayBeforeRetry);
        } else {
          // In delayed retry phase
          delayedAttempts++;
          if (delayedAttempts < maxDelayedAttempts) {
            setTimeout(findElement, delayedRetryInterval);
          } else {
            console.warn(`Tutorial: Target element not found after all attempts: ${targetSelector}`);
            // Notify parent component that element was not found
            if (onElementNotFound) {
              onElementNotFound();
            }
          }
        }
      }
    };

    // Start searching after initial delay
    const initialTimer = setTimeout(() => {
      findElement();
    }, initialDelay);

    // Cleanup: Reset styles when component unmounts or targetSelector changes
    return () => {
      clearTimeout(initialTimer);
      if (currentElement) {
        // Only reset position if we changed it (it was originally static)
        const computedPosition = window.getComputedStyle(currentElement).position;
        if (currentElement.style.position === 'relative' && computedPosition === 'relative') {
          currentElement.style.position = '';
        }
        currentElement.style.zIndex = '';
      }
    };
  }, [targetSelector]);

  // Show loading overlay while searching for target element
  if (!targetElement || !targetRect) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Loading message - optional, can be removed if you prefer just the dark overlay */}
      </Box>
    );
  }

  return (
    <>
      {/* Dark backdrop overlay - blocks clicks to prevent navigation */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9998,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: `${targetRect.top - 8}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Left overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.top - 8}px`,
            left: 0,
            width: `${targetRect.left - 8}px`,
            height: `${targetRect.height + 16}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Right overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.top - 8}px`,
            left: `${targetRect.right + 8}px`,
            right: 0,
            height: `${targetRect.height + 16}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Bottom overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.bottom + 8}px`,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        />
        {/* Highlight border around target */}
        <Box
          sx={{
            position: 'absolute',
            top: `${targetRect.top - 8}px`,
            left: `${targetRect.left - 8}px`,
            width: `${targetRect.width + 16}px`,
            height: `${targetRect.height + 16}px`,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: '8px',
            boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.2)',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Tooltip popover */}
      {position?.startsWith('inside-') ? (
        // Inside positioning - place dialog inside the highlighted area
        <Box
          ref={popperRef}
          sx={{
            position: 'fixed',
            zIndex: 10000,
            ...(position === 'inside-top' && {
              top: `${targetRect.top}px`,
              left: `${targetRect.left + (targetRect.width - 400) / 2}px`,
              maxWidth: Math.min(400, targetRect.width - 32),
            }),
            ...(position === 'inside-bottom' && {
              bottom: `${window.innerHeight - targetRect.bottom}px`,
              left: `${targetRect.left + (targetRect.width - 400) / 2}px`,
              maxWidth: Math.min(400, targetRect.width - 32),
            }),
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 3,
              backgroundColor: 'white',
              borderRadius: 2,
              position: 'relative',
              zIndex: 10000,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {content}
            </Typography>
            {children}
          </Paper>
        </Box>
      ) : (
        // Standard Popper positioning - outside the highlighted area
        <Popper
          open={true}
          anchorEl={targetElement}
          placement={position as 'top' | 'bottom' | 'left' | 'right'}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 16],
              },
            },
          ]}
          sx={{ zIndex: 10000 }}
        >
          <Paper
            ref={popperRef}
            elevation={8}
            sx={{
              maxWidth: 400,
              p: 3,
              backgroundColor: 'white',
              borderRadius: 2,
              position: 'relative',
              zIndex: 10000,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {content}
            </Typography>
            {children}
          </Paper>
        </Popper>
      )}
    </>
  );
};

export default TutorialSpotlight;
